import { EQUIPMENT_GROUPS_ENDPOINT } from "../../../../../consts";
import { useCRUD } from "../../../../../api/crud";
import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import CustomCircularProgress from "../../../../../components/ui/customCircularProgress/customCircularProgress.component";
import { Box, Typography, Divider, Chip } from "@mui/material";
import { basicInfoContainerStyle } from "../../../../../styles/commonStyles";
import { useTranslation } from "react-i18next";
import { useHasPermission } from "../../../../../contexts/auth";
import { formatDateTime } from "../../../../../utils";
import { useBreadcrumbs } from "../../../../../components/ui/breadcrumps/breadcrumbsContext";

interface EquipmentModel {
  id: string;
  display_name?: string;
  name?: string;
}

interface EquipmentGroup {
  id: string;
  name: string;
  equipment_models: EquipmentModel[];
  created_at: string;
  updated_at: string;
}

export const EquipmentGroupBasicInfo = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { get } = useCRUD(EQUIPMENT_GROUPS_ENDPOINT);
  const { setOverride, clearOverride } = useBreadcrumbs();
  const [group, setGroup] = useState<EquipmentGroup | null>(null);
  const canViewEquipmentGroups = useHasPermission("service.view_equipmentgroup");

  useEffect(() => {
    if (!id || !canViewEquipmentGroups) {
      return undefined;
    }

    const path = `/service/statistics/equipment-groups/${id}`;

    const fetchGroup = async () => {
      const response = await get(id);
      if (response.data) {
        setGroup(response.data);
        setOverride(path, response.data.name || id);
      }
    };

    const handleUpdated = () => {
      fetchGroup();
    };

    window.addEventListener("equipmentGroupUpdated", handleUpdated);
    fetchGroup();
    return () => {
      window.removeEventListener("equipmentGroupUpdated", handleUpdated);
      clearOverride(path);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, canViewEquipmentGroups]);

  if (!canViewEquipmentGroups) {
    return null;
  }

  if (!group) return <CustomCircularProgress />;

  return (
    <Box sx={basicInfoContainerStyle}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
          {group.name}
        </Typography>
      </Box>

      <Divider />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          {t("Assigned Equipment Models")}
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {group.equipment_models.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              {t("No equipment models assigned to this group yet")}
            </Typography>
          )}
          {group.equipment_models.map((equipmentModel) => (
            <Chip
              key={equipmentModel.id}
              label={equipmentModel.display_name || equipmentModel.name}
            />
          ))}
        </Box>
      </Box>

      <Divider />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          {t("Metadata")}
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>{t("Created At")}:</strong> {formatDateTime(group.created_at)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>{t("Updated At")}:</strong> {formatDateTime(group.updated_at)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
